// src/store/documentStore.ts
import { create } from "zustand";
import { db, storage } from "@/lib/firebase";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  arrayUnion,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useAuthStore } from "./authStore";
import { useNotificationStore } from "./notificationStore";
import { APPROVAL_CHAINS_BY_ID } from "@/constants/users";
import type { Document } from "@/types/document";
import { toast } from "@/hooks/use-toast";
import confetti from "canvas-confetti";

interface DocumentState {
  documents: Document[];
  myDocuments: Document[];
  documentsForApproval: Document[];
  loading: boolean;
  error: string | null;
  unsubscribe: (() => void) | null;

  init: () => void;
  cleanup: () => void;

  createDocument: (data: Partial<Document>, file?: File) => Promise<string | null>;
  approve: (docId: string, comment?: string) => Promise<void>;
  reject: (docId: string, comment: string) => Promise<void>;
  signDocument: (docId: string) => Promise<void>;
  uploadFile: (file: File) => Promise<string>;
}

export const useDocumentStore = create<DocumentState>()((set, get) => ({
  documents: [],
  myDocuments: [],
  documentsForApproval: [],
  loading: false,
  error: null,
  unsubscribe: null,

  init: () => {
    const user = useAuthStore.getState().user;
    if (!user) return;

    const userId = String(user.uid || user.id || "");

    set({ loading: true });

    const q = query(collection(db, "documents"), orderBy("createdAt", "desc"));

    const unsub = onSnapshot(q, (snap) => {
      const allDocs = snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          ownerId: String(data.ownerId || ""),
          approvers: (data.approvers || []).map(String).filter(Boolean),
          currentApproverIndex: Number(data.currentApproverIndex ?? 0),
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
          history: (data.history || []).map((h: any) => ({
            ...h,
            userId: String(h.userId || ""),
            date: h.date?.toDate?.() || new Date(),
          })),
          unreadBy: Array.isArray(data.unreadBy) ? data.unreadBy.map(String) : [],
          unreadMessagesBy: data.unreadMessagesBy || {},
        } as Document;
      });

      const visibleDocs = allDocs.filter((d) =>
        d.ownerId === userId || d.approvers.some((appr) => appr === userId)
      );

      const myDocs = visibleDocs.filter((d) => d.ownerId === userId);
      const forApproval = visibleDocs.filter((d) => {
        if (d.status !== "pending") return false;
        const idx = d.currentApproverIndex;
        return String(d.approvers[idx] || "") === userId;
      });

      set({
        documents: visibleDocs,
        myDocuments: myDocs,
        documentsForApproval: forApproval,
        loading: false,
      });
    });

    set({ unsubscribe: unsub });
  },

  cleanup: () => {
    const unsub = get().unsubscribe;
    if (unsub) unsub();
    set({ 
      documents: [], 
      myDocuments: [], 
      documentsForApproval: [], 
      loading: false, 
      unsubscribe: null 
    });
  },

  createDocument: async (data, file) => {
    try {
      let fileUrl = "";
      let fileName = "";

      if (file) {
        fileName = file.name;
        const storagePath = `documents/${Date.now()}_${file.name}`;
        const fileRef = ref(storage, storagePath);
        await uploadBytes(fileRef, file);
        fileUrl = await getDownloadURL(fileRef);
      }

      const user = useAuthStore.getState().user;
      if (!user) return null;

      const ownerId = String(user.uid || user.id || "");
      const ownerName = user.name || "Неизвестный";

      const approvers = (APPROVAL_CHAINS_BY_ID[Number(ownerId)] || []).map(String).filter(Boolean);

      const initialUnreadBy = approvers.length > 0 ? [approvers[0]] : [];

      const docRef = await addDoc(collection(db, "documents"), {
        ...data,
        ownerId,
        ownerName,
        status: approvers.length > 0 ? "pending" : "draft",
        approvers,
        currentApproverIndex: 0,
        fileUrl,
        fileName,
        unreadBy: initialUnreadBy,
        unreadMessagesBy: {},
        history: [{
          action: "created",
          userId: ownerId,
          userName: ownerName,
          date: new Date(),
        }],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      if (initialUnreadBy.length > 0) {
        const notificationStore = useNotificationStore.getState();
        notificationStore.incrementDocumentNotifications(1);
      }

      toast({ title: "Успех", description: "Документ создан и отправлен на согласование" });
      return docRef.id;
    } catch (e: any) {
      console.error("Ошибка создания документа:", e);
      toast({ title: "Ошибка", description: "Не удалось создать документ", variant: "destructive" });
      return null;
    }
  },

  approve: async (docId: string, comment = "") => {
    const user = useAuthStore.getState().user;
    if (!user) return;

    const userId = String(user.uid || user.id || "");
    const userName = user.name || "Пользователь";

    let docState = get().documents.find(d => d.id === docId) ||
                   get().myDocuments.find(d => d.id === docId) ||
                   get().documentsForApproval.find(d => d.id === docId);

    if (!docState) return;

    const currentIndex = Number(docState.currentApproverIndex);
    const nextIndex = currentIndex + 1;
    const isLast = nextIndex >= docState.approvers.length;

    const newStatus = isLast ? "approved" : "pending";
    const nextApproverId = isLast ? null : String(docState.approvers[nextIndex] || "");

    const historyEntry = {
      action: isLast ? "approved" : "forwarded",
      userId,
      userName,
      date: new Date(),
      ...(comment.trim() && { comment: comment.trim() }),
    };

    const newUnreadBy = nextApproverId ? [nextApproverId] : [];

    const newUnreadMessagesBy: Record<string, number> = {};
    if (nextApproverId) newUnreadMessagesBy[nextApproverId] = 1;
    newUnreadMessagesBy[docState.ownerId] = (docState.unreadMessagesBy?.[docState.ownerId] || 0) + 1;

    try {
      await updateDoc(doc(db, "documents", docId), {
        status: newStatus,
        currentApproverIndex: nextIndex,
        history: arrayUnion(historyEntry),
        updatedAt: serverTimestamp(),
        unreadBy: newUnreadBy,
        unreadMessagesBy: newUnreadMessagesBy,
      });

      const systemMessage = {
        text: isLast 
          ? `✅ Документ полностью согласован ${userName}`
          : `➡️ ${userName} согласовал и передал дальше`,
        senderId: "system",
        senderName: "Система",
        timestamp: serverTimestamp(),
        isSystem: true,
      };

      await addDoc(collection(db, "documents", docId, "messages"), systemMessage);

      toast({
        title: isLast ? "Готово!" : "Передано дальше",
        description: isLast ? "Документ полностью согласован" : "Документ передан следующему"
      });

      if (isLast) confetti({ particleCount: 120, spread: 90, origin: { y: 0.6 } });
    } catch (e: any) {
      console.error("[approve] Ошибка:", e);
      toast({ title: "Ошибка", description: "Не удалось согласовать документ", variant: "destructive" });
    }
  },

  reject: async (docId: string, comment: string) => {
    const user = useAuthStore.getState().user;
    if (!user) return;

    const userId = String(user.uid || user.id || "");
    const userName = user.name || "Пользователь";

    if (!comment.trim()) {
      toast({ title: "Ошибка", description: "Укажите причину отклонения", variant: "destructive" });
      return;
    }

    const docState = get().documents.find(d => d.id === docId) ||
                     get().myDocuments.find(d => d.id === docId) ||
                     get().documentsForApproval.find(d => d.id === docId);

    if (!docState) return;

    const newUnreadBy = [String(docState.ownerId)];

    const newUnreadMessagesBy: Record<string, number> = {
      [docState.ownerId]: (docState.unreadMessagesBy?.[docState.ownerId] || 0) + 1
    };

    try {
      await updateDoc(doc(db, "documents", docId), {
        status: "rejected",
        history: arrayUnion({
          action: "rejected",
          userId,
          userName,
          comment: comment.trim(),
          date: new Date(),
        }),
        updatedAt: serverTimestamp(),
        unreadBy: newUnreadBy,
        unreadMessagesBy: newUnreadMessagesBy,
      });

      const systemMessage = {
        text: `🔄 ${userName} отправил документ на доработку\nПричина: ${comment.trim()}`,
        senderId: "system",
        senderName: "Система",
        timestamp: serverTimestamp(),
        isSystem: true,
      };

      await addDoc(collection(db, "documents", docId, "messages"), systemMessage);

      toast({ title: "На доработку", description: "Документ отправлен на доработку", variant: "destructive" });
    } catch (e) {
      console.error("Ошибка reject:", e);
      toast({ title: "Ошибка", description: "Не удалось отправить на доработку", variant: "destructive" });
    }
  },

  signDocument: async (docId) => {
    console.warn("signDocument пока не реализован");
  },

  uploadFile: async (file) => {
    const storagePath = `documents-temp/${Date.now()}_${file.name}`;
    const fileRef = ref(storage, storagePath);
    await uploadBytes(fileRef, file);
    return await getDownloadURL(fileRef);
  },
}));